import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Dialog, DialogContent, DialogPortal, DialogOverlay,
} from "../ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import {
  X, Plus, Minus, Trash2, ShoppingBag, Lock, ShieldCheck,
  Wallet, Sparkles, Tag, Truck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { useTheme } from "../../context/ThemeContext";
import { computeShipping, amountToFreeShipping, zoneNameForProvince, zoneCapsForProvince, codProvinces, freeShippingProvinces, formatProvinceList } from "../../utils/shipping";
import { comuniForProvince, comuniListText } from "../../utils/comuni";
import { getBundleSuggestion, applyBundleUpgrade } from "../../utils/bundleSuggestion";
import { isPrAccount, prDiscountPct, prAutoDiscountAmount, prBestOverride, pctLabel } from "../../utils/prDiscount";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All Italian provinces for the picker (sigla code)
const ITALIAN_PROVINCES = [
  "AG","AL","AN","AO","AR","AP","AT","AV","BA","BT","BL","BN","BG","BI","BO","BZ","BS","BR","CA","CL","CB","CI","CE","CT","CZ","CH","CO","CS","CR","KR","CN","EN","FM","FE","FI","FG","FC","FR","GE","GO","GR","IM","IS","SP","AQ","LT","LE","LC","LI","LO","LU","MC","MN","MS","MT","ME","MI","MO","MB","NA","NO","NU","OG","OT","OR","PD","PA","PR","PV","PG","PU","PE","PC","PI","PT","PN","PZ","PO","RG","RA","RC","RE","RI","RN","RM","RO","SA","VS","SS","SV","SI","SR","SO","TA","TE","TR","TO","TP","TN","TV","TS","UD","VA","VE","VB","VC","VR","VV","VI","VT",
];

const ProvincePicker = ({ value, onChange }) => (
  <select
    value={value || ""}
    onChange={(e) => onChange(e.target.value)}
    data-testid="cart-province-picker"
    className="bg-[#060A14] border border-white/10 rounded-sm px-3 py-2 text-white text-[12px] uppercase tracking-[0.16em] focus:outline-none focus:border-[#C0C0C0]"
  >
    <option value="">Seleziona provincia…</option>
    {ITALIAN_PROVINCES.map((p) => (
      <option key={p} value={p}>{p}</option>
    ))}
  </select>
);

const LineItem = ({ item, onQty, onRemove, hidePrices }) => {
  const isExternalPhoto = item.image && (item.image.startsWith("http") || item.image.startsWith("/api/uploads"));
  const key = item.variant_key || item.product_id;
  const variantLabel = [item.flavor, item.size].filter(Boolean).join(" · ");
  return (
    <div data-testid={`cart-line-${item.product_id}`}
         className="flex items-start gap-4 py-4 border-b border-white/5 last:border-b-0">
      <div className={`w-16 h-20 shrink-0 rounded overflow-hidden ${
        isExternalPhoto ? "bg-white" : "bg-[#060A14]"
      } flex items-center justify-center`}>
        {item.image
          ? <img src={item.image} alt={item.name} className={`max-w-full max-h-full ${isExternalPhoto ? "object-contain p-1" : "object-cover w-full h-full"}`} />
          : <ShoppingBag className="w-6 h-6 text-[#94A3B8]" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.28em] uppercase text-[#94A3B8]">{item.brand}</p>
            <p className="font-[Outfit] text-white text-[15px] tracking-tight leading-snug mt-0.5 truncate">{item.name}</p>
            {variantLabel && (
              <p data-testid={`cart-variant-${item.product_id}`}
                 className="mt-1 inline-block text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full bg-[#C0C0C0]/10 border border-[#C0C0C0]/20 text-[#E5E4E2]">
                {variantLabel}
              </p>
            )}
            {item.bundle_name && (
              <p data-testid={`cart-bundle-badge-${item.product_id}`}
                 className="mt-1 inline-block text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 ml-1">
                Kit: {item.bundle_name}
              </p>
            )}
          </div>
          <button onClick={() => onRemove(key)} data-testid={`cart-remove-${item.product_id}`}
                  aria-label="Rimuovi" className="text-[#94A3B8] hover:text-red-300 shrink-0 p-1">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
        <div className="mt-2 flex items-center justify-between">
          <div className="inline-flex items-center border border-white/15 rounded-sm">
            <button onClick={() => onQty(key, item.quantity - 1)}
                    disabled={item.quantity <= 1}
                    data-testid={`cart-dec-${item.product_id}`}
                    className="px-2.5 py-1.5 text-[#E5E4E2] hover:bg-white/5 disabled:opacity-30">
              <Minus className="w-3.5 h-3.5" />
            </button>
            <span data-testid={`cart-qty-${item.product_id}`} className="px-3 text-[13px] text-white font-[Outfit]">{item.quantity}</span>
            <button onClick={() => onQty(key, item.quantity + 1)}
                    data-testid={`cart-inc-${item.product_id}`}
                    className="px-2.5 py-1.5 text-[#E5E4E2] hover:bg-white/5">
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>
          <span className="text-[13px] text-white font-[Outfit]" data-testid={`cart-line-price-${item.product_id}`}>
            {hidePrices
              ? <span className="text-[10px] tracking-[0.22em] uppercase text-[#94A3B8]">Su consulenza</span>
              : (
                <>
                  {item.original_price > item.price && (
                    <span className="text-[#94A3B8] line-through mr-1.5 text-[11px]">
                      €{(Number(item.original_price) * item.quantity).toFixed(2)}
                    </span>
                  )}
                  {`€${(Number(item.price) * item.quantity).toFixed(2)}`}
                </>
              )}
          </span>
        </div>
      </div>
    </div>
  );
};

const CartDrawer = () => {
  const cart = useCart();
  const { user, userApi } = useAuth();
  const { settings } = useTheme();
  const navigate = useNavigate();
  const hidePrices = settings?.layout?.hide_prices;
  const [privacyOk, setPrivacyOk] = useState(false);
  const [marketingOk, setMarketingOk] = useState(false);
  const [consentErr, setConsentErr] = useState("");
  const [sdi, setSdi] = useState("");
  const [pec, setPec] = useState("");
  const [codeInput, setCodeInput] = useState("");
  // When a code is rejected for requiring a login, keep it typed in the box
  // (the drawer has no inline login dialog — a link to /login is enough;
  // clearing it would force the customer to retype it after signing in).
  const applyCode = async () => {
    const res = await cart.addDiscountCode(codeInput);
    if (!res?.valid && res?.login_required) return; // keep typed so retry after login is easy
    setCodeInput("");
  };

  // Kit suggeriti: quando il cliente ha già 2+ prodotti di un kit attivo nel
  // carrello, gli proponiamo di attivarlo (sostituisce le righe singole con
  // il kit allo stesso prezzo fisso/scontato). Catalogo+kit caricati on-demand.
  const [bundles, setBundles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [bundlesLoaded, setBundlesLoaded] = useState(false);

  useEffect(() => {
    if (!cart.open || bundlesLoaded || cart.items.length < 2) return;
    let alive = true;
    (async () => {
      try {
        const [b, c] = await Promise.all([
          axios.get(`${API}/bundles`),
          axios.get(`${API}/catalog`),
        ]);
        if (alive) { setBundles(b.data || []); setCatalog(c.data || []); setBundlesLoaded(true); }
      } catch {
        if (alive) setBundlesLoaded(true);
      }
    })();
    return () => { alive = false; };
  }, [cart.open, cart.items.length, bundlesLoaded]);

  const productsById = useMemo(() => {
    const map = {};
    catalog.forEach((p) => { map[p.id] = p; });
    return map;
  }, [catalog]);

  const bundleSuggestion = useMemo(
    () => getBundleSuggestion(bundles, productsById, cart.items),
    [bundles, productsById, cart.items]
  );

  const activateSuggestion = (bundle) => applyBundleUpgrade(cart, bundle, productsById);

  const isPr = isPrAccount(user);
  const prPct = prDiscountPct(user);
  const prAutoAmount = prAutoDiscountAmount(user, cart.subtotal);
  // PR accounts never stack multiple codes — only the single BEST admin-flagged
  // "pr_override" code (if any) can beat the automatic discount, mirroring the backend
  // and CheckoutPage.jsx exactly, so the drawer preview always matches the final total.
  const cartBestOverride = isPr ? prBestOverride(cart.discounts, cart.subtotal) : null;
  const cartPrOverrideActive = isPr && !!cartBestOverride && cartBestOverride.amount > prAutoAmount;
  const cartDiscountAmount = isPr
    ? Math.min(cartPrOverrideActive ? cartBestOverride.amount : prAutoAmount, cart.subtotal)
    : (cart.discountValid ? Math.min(cart.discountAmount, cart.subtotal) : 0);
  const cartAfterDiscount = Math.max(0, cart.subtotal - cartDiscountAmount);
  const cartFreeByDiscount = isPr ? (cartPrOverrideActive && cartBestOverride.free_shipping) : (cart.discountValid && cart.discountFreeShipping);
  const cartProvinceUpper = (cart.province || "").toUpperCase();
  const cartZoneCaps = zoneCapsForProvince(cart.province, settings);
  const cartComuni = comuniForProvince(cart.province, settings);
  const cartComuneName = comuniListText(cartZoneCaps, settings);
  const cartHasCapZone = cartZoneCaps.length > 0 && !!cartComuneName;
  // Representative postal code when the customer confirms the comune (preview only —
  // the real address postal code is what gets validated authoritatively at checkout).
  const cartEffectiveCap = cartHasCapZone && cart.comuneConfirmed ? cartZoneCaps[0] : "";
  const cartShipping = computeShipping(cartAfterDiscount, settings, cartFreeByDiscount, cart.province, cartEffectiveCap);
  const cartToFree = amountToFreeShipping(cartAfterDiscount, settings);
  const cartZoneName = zoneNameForProvince(cart.province, settings, cartEffectiveCap);
  const freeShipList = freeShippingProvinces(settings);
  const codList = codProvinces(settings);
  const cartProvinceHasPerk = freeShipList.includes(cartProvinceUpper) || codList.includes(cartProvinceUpper);

  const handleOpenChange = (val) => cart.setOpen(val);
  const close = () => cart.setOpen(false);

  const goToCheckout = async () => {
    if (!privacyOk) {
      setConsentErr("Devi accettare la Privacy Policy per procedere all'ordine.");
      return;
    }
    setConsentErr("");
    // If logged in, persist consent update server-side (best-effort)
    if (user && userApi) {
      try {
        await userApi.patch("/auth/consents", {
          privacy_accepted: true,
          marketing_authorized: marketingOk,
        });
        if (sdi.trim() || pec.trim()) {
          await userApi.patch("/auth/profile", {
            codice_sdi: sdi.trim() || undefined,
            pec_fatturazione: pec.trim() || undefined,
          });
        }
      } catch {
        /* silent — do not block checkout on consent-sync errors */
      }
    }
    cart.setOpen(false);
    navigate("/checkout");
  };

  return (
    <Dialog open={cart.open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogOverlay className="fixed inset-0 z-50 bg-[#060A14]/85 backdrop-blur-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          data-testid="cart-drawer"
          className="fixed right-0 top-0 h-full w-full sm:w-[480px] bg-[#0A1121] border-l border-white/10 shadow-2xl z-50 flex flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
        >
          <DialogPrimitive.Title className="sr-only">Carrello</DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            Lista prodotti selezionati nel carrello, pronti per il checkout sicuro con pagamento Stripe.
          </DialogPrimitive.Description>

          {/* Header */}
          <header className="px-6 py-5 border-b border-white/10 flex items-center justify-between">
            <div>
              <span className="text-[10px] tracking-[0.32em] uppercase text-[#C0C0C0]">Carrello</span>
              <h2 className="mt-1 font-[Outfit] text-white text-2xl tracking-tight font-light">
                {cart.itemCount === 0 ? "Vuoto" : `${cart.itemCount} ${cart.itemCount === 1 ? "articolo" : "articoli"}`}
              </h2>
            </div>
            <button onClick={close} aria-label="Chiudi" data-testid="cart-close"
                    className="text-[#94A3B8] hover:text-white p-2">
              <X className="w-5 h-5" />
            </button>
          </header>

          {/* Checkout notice */}
          {cart.items.length > 0 && (
            <div className="px-6 py-3 border-b border-white/10 bg-[#0D1426] flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#E5E4E2]"
                 data-testid="cart-quote-notice">
              <ShieldCheck className="w-3 h-3 text-emerald-400" /> Checkout sicuro · pagamento con Stripe
            </div>
          )}

          {/* Body */}
          <div className="flex-1 min-h-0 overflow-y-auto px-6">
            {cart.items.length === 0 ? (
              <div className="py-16 text-center" data-testid="cart-empty">
                <ShoppingBag className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" strokeWidth={1.2} />
                <p className="text-[#E5E4E2] font-[Outfit] text-lg">Il tuo carrello è vuoto</p>
                <p className="text-[#94A3B8] text-[12px] mt-2">
                  Aggiungi i tuoi integratori preferiti per procedere all'ordine.
                </p>
                <button onClick={close} className="mt-6 inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#C0C0C0] hover:text-white">
                  Esplora il catalogo
                </button>
              </div>
            ) : (
              <div className="py-2">
                {cart.items.map((it) => (
                  <LineItem key={it.variant_key || it.product_id} item={it} hidePrices={hidePrices}
                            onQty={cart.setQuantity} onRemove={cart.removeItem} />
                ))}
              </div>
            )}
          </div>

          {/* Footer: province + Leo CTA only */}
          {cart.items.length > 0 && (
            <footer className="flex-shrink-0 max-h-[55vh] overflow-y-auto border-t border-white/10 bg-[#060A14]">
              {/* Kit suggerito */}
              {bundleSuggestion && (
                <div className="px-6 py-4 border-b border-white/5 bg-gradient-to-br from-emerald-500/10 to-transparent" data-testid="cart-bundle-suggestion">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[11px] text-emerald-200 leading-relaxed">
                        Hai {bundleSuggestion.matched} prodotti del{" "}
                        <span className="text-white font-medium" data-testid="cart-bundle-suggestion-name">{bundleSuggestion.bundle.name}</span>
                        {bundleSuggestion.matched < bundleSuggestion.total ? " nel carrello." : " nel carrello — hai già tutto!"}
                      </p>
                      {!hidePrices && bundleSuggestion.savings > 0 && (
                        <p className="mt-1 text-[10px] text-[#94A3B8]" data-testid="cart-bundle-suggestion-savings">
                          Attiva il kit e risparmia €{bundleSuggestion.savings.toFixed(2)}
                        </p>
                      )}
                      <button onClick={() => activateSuggestion(bundleSuggestion.bundle)} data-testid="cart-bundle-suggestion-cta"
                              className="mt-3 inline-flex items-center gap-1.5 bg-emerald-500/15 border border-emerald-400/40 text-emerald-200 px-3.5 py-2 rounded-sm text-[10px] tracking-[0.2em] uppercase font-medium hover:bg-emerald-500/25 transition-colors">
                        <Sparkles className="w-3 h-3" /> Attiva il Kit
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Province picker */}
              <div className="px-6 py-4 border-b border-white/5">
                <div className="flex items-center justify-between gap-3">
                  <label className="text-[10px] tracking-[0.28em] uppercase text-[#94A3B8]">Provincia di consegna</label>
                  <div className="flex items-center gap-2">
                    <button type="button" onClick={() => cart.setProvince("SR")} data-testid="cart-province-quick-sr"
                            className={`px-2.5 py-1.5 rounded-sm text-[10px] tracking-[0.1em] border transition-colors ${
                              cartProvinceUpper === "SR" ? "border-emerald-400/60 bg-emerald-500/10 text-emerald-300" : "border-white/15 text-[#94A3B8] hover:text-white hover:border-white/30"
                            }`}>
                      SR
                    </button>
                    <button type="button" onClick={() => cart.setProvince("CT")} data-testid="cart-province-quick-ct"
                            className={`px-2.5 py-1.5 rounded-sm text-[10px] tracking-[0.1em] border transition-colors ${
                              cartProvinceUpper === "CT" ? "border-emerald-400/60 bg-emerald-500/10 text-emerald-300" : "border-white/15 text-[#94A3B8] hover:text-white hover:border-white/30"
                            }`}>
                      CT
                    </button>
                    <ProvincePicker value={cart.province} onChange={cart.setProvince} />
                  </div>
                </div>

                {cartHasCapZone && (
                  <label className="mt-3 flex items-start gap-2.5 cursor-pointer" data-testid="cart-comune-checkbox-label">
                    <input
                      type="checkbox"
                      checked={cart.comuneConfirmed}
                      onChange={(e) => cart.setComuneConfirmed(e.target.checked)}
                      data-testid="cart-comune-checkbox"
                      className="mt-0.5 w-3.5 h-3.5 accent-emerald-400 cursor-pointer shrink-0"
                    />
                    <span className="text-[11px] leading-relaxed text-[#CBD5E1]">
                      Consegna {cartComuni.length === 1 ? "nel comune di" : "nei comuni di"} {cartComuneName}
                    </span>
                  </label>
                )}
                {cartHasCapZone && !cart.comuneConfirmed && (
                  <p className="mt-2 text-[10px] text-amber-300" data-testid="cart-comune-warning">
                    La spedizione gratuita vale solo per {cartComuni.length === 1 ? "il comune di" : "i comuni di"} {cartComuneName}, non per gli altri paesi della provincia.
                  </p>
                )}

                {cart.province && !cartProvinceHasPerk && (
                  <p className="mt-2 text-[10px] text-[#94A3B8]" data-testid="cart-province-note">
                    Spedizione gratuita nei comuni di {comuniListText([...new Set(freeShipList.flatMap((p) => zoneCapsForProvince(p, settings)))], settings) || "Siracusa e Catania"} · pagamento alla
                    consegna disponibile solo per {formatProvinceList(codList) || "—"}.
                  </p>
                )}
                {cart.province && cartProvinceHasPerk && (
                  <p className="mt-2 inline-flex items-center gap-1.5 text-[10px] text-emerald-200" data-testid="cart-cod-banner">
                    <Wallet className="w-3 h-3" />
                    {cartProvinceUpper}: chiedi a Leo lo sconto spedizione e il pagamento alla consegna.
                  </p>
                )}
              </div>

              {/* Discount codes + price summary */}
              {!hidePrices && (
                <div className="px-6 py-4 border-b border-white/5" data-testid="cart-discount-section">
                  <div className="flex items-stretch gap-2">
                    <div className="relative flex-1">
                      <Tag className="w-3.5 h-3.5 text-[#94A3B8] absolute left-3 top-1/2 -translate-y-1/2" />
                      <input
                        value={codeInput}
                        onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                        onKeyDown={(e) => { if (e.key === "Enter") applyCode(); }}
                        placeholder="Codice sconto"
                        data-testid="cart-discount-input"
                        className="w-full bg-[#0A1121] border border-white/10 rounded-sm pl-9 pr-3 py-2.5 text-white text-[12px] uppercase tracking-[0.16em] focus:outline-none focus:border-[#C0C0C0] placeholder:text-[#94A3B8]/60"
                      />
                    </div>
                    <button onClick={applyCode} disabled={cart.discountLoading || !codeInput.trim()} data-testid="cart-discount-apply"
                            className="px-4 rounded-sm text-[10px] tracking-[0.22em] uppercase bg-[#C0C0C0] text-[#060A14] font-medium hover:bg-white disabled:opacity-40">
                      {cart.discountLoading ? "…" : "Applica"}
                    </button>
                  </div>
                  {cart.discountError && (
                    <p className="mt-2 text-[10px] text-red-300 flex items-center gap-2 flex-wrap" data-testid="cart-discount-error">
                      <span>{cart.discountError.message}</span>
                      {cart.discountError.login_required && (
                        <Link to="/login" data-testid="cart-discount-login-link"
                              className="underline underline-offset-2 hover:text-white font-medium text-red-200">
                          Accedi o registrati
                        </Link>
                      )}
                    </p>
                  )}
                  {cart.discountCodes.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5" data-testid="cart-discount-chips">
                      {cart.discountCodes.map((code) => {
                        const d = cart.discounts.find((x) => x?.code === code);
                        return (
                        <span key={code} data-testid={`cart-discount-chip-${code}`}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-400/30 text-[10px] tracking-[0.1em] text-emerald-200">
                          {code}
                          {d && d.valid && d.stack_mode === "exclusive" && (
                            <span data-testid={`cart-discount-chip-exclusive-${code}`}
                                  className="px-1 py-px rounded-full text-[8px] tracking-[0.12em] uppercase bg-amber-500/15 border border-amber-400/30 text-amber-200">
                              Esclusivo
                            </span>
                          )}
                          <button onClick={() => cart.removeDiscountCode(code)} data-testid={`cart-discount-chip-remove-${code}`}
                                  className="hover:text-white" aria-label={`Rimuovi codice ${code}`}>
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                        );
                      })}
                    </div>
                  )}

                  <dl className="mt-4 space-y-1.5 text-[12px]">
                    <div className="flex items-center justify-between text-[#94A3B8]">
                      <dt>Subtotale</dt>
                      <dd className="font-[Outfit] text-[#E5E4E2]" data-testid="cart-subtotal">€{cart.subtotal.toFixed(2)}</dd>
                    </div>
                    {cartDiscountAmount > 0 && (
                      <div className="flex items-center justify-between text-emerald-300" data-testid="cart-discount-row">
                        <dt>{isPr ? (cartPrOverrideActive ? `Sconto PR speciale (${cartBestOverride.code})` : `Sconto PR a vita (${pctLabel(prPct)}%)`) : `Sconto (${cart.discountCodes.join(", ")})`}</dt>
                        <dd className="font-[Outfit]">−€{cartDiscountAmount.toFixed(2)}</dd>
                      </div>
                    )}
                    {isPr && cart.discountCodes.length > 0 && !cartPrOverrideActive && (
                      <p className="text-[10px] text-amber-300" data-testid="cart-pr-code-note">
                        Sei un account PR: vale solo lo sconto automatico del {pctLabel(prPct)}%, non cumulabile con i codici sconto.
                      </p>
                    )}
                    <div className="flex items-center justify-between text-[#94A3B8]">
                      <dt>Spedizione{cartZoneName ? <span className="text-[10px] text-[#94A3B8]/80"> ({cartZoneName})</span> : ""}</dt>
                      <dd className={`font-[Outfit] ${cartShipping === 0 ? "text-emerald-300" : "text-[#E5E4E2]"}`} data-testid="cart-shipping">
                        {cartShipping === 0 ? "Gratuita" : `€${cartShipping.toFixed(2)}`}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between pt-2 mt-1 border-t border-white/10">
                      <dt className="text-white tracking-[0.16em] uppercase text-[11px]">Totale</dt>
                      <dd className="font-[Outfit] text-white text-lg" data-testid="cart-total">
                        €{(cartAfterDiscount + cartShipping).toFixed(2)}
                      </dd>
                    </div>
                  </dl>
                  {cartShipping > 0 && cartToFree > 0 && (
                    <p className="mt-2 flex items-center gap-1.5 text-[10px] text-[#C0C0C0] tracking-[0.14em]" data-testid="cart-free-ship-hint">
                      <Truck className="w-3 h-3" /> Ti mancano €{cartToFree.toFixed(2)} alla spedizione gratuita
                    </p>
                  )}
                  <p className="mt-2 text-[9px] text-[#94A3B8] tracking-[0.14em]">Paghi in modo sicuro con Stripe al passaggio successivo.</p>
                </div>
              )}

              {/* Electronic invoicing (optional) */}
              <div className="px-6 py-4 border-b border-white/5" data-testid="cart-invoicing-fields">
                <p className="text-[10px] tracking-[0.28em] uppercase text-[#94A3B8] mb-3">
                  Fatturazione elettronica <span className="text-emerald-300/70 normal-case tracking-normal">(opzionale)</span>
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    value={sdi}
                    onChange={(e) => setSdi(e.target.value.toUpperCase().slice(0, 7))}
                    placeholder="Codice SDI"
                    maxLength={7}
                    data-testid="cart-sdi-input"
                    className="bg-[#0A1121] border border-white/10 rounded-sm px-3 py-2.5 text-white text-[12px] focus:outline-none focus:border-[#C0C0C0] placeholder:text-[#94A3B8]/60 uppercase"
                  />
                  <input
                    value={pec}
                    onChange={(e) => setPec(e.target.value)}
                    placeholder="PEC per Fatturazione"
                    type="email"
                    data-testid="cart-pec-input"
                    className="bg-[#0A1121] border border-white/10 rounded-sm px-3 py-2.5 text-white text-[12px] focus:outline-none focus:border-[#C0C0C0] placeholder:text-[#94A3B8]/60"
                  />
                </div>
              </div>

              {/* CTA */}
              <div className="px-6 pb-5 pt-4 grid grid-cols-1 gap-2.5">
                {/* GDPR consents at checkout */}
                <div data-testid="cart-consents" className="space-y-2 mb-2">
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={privacyOk}
                      onChange={(e) => setPrivacyOk(e.target.checked)}
                      data-testid="cart-privacy-checkbox"
                      className="mt-0.5 w-3.5 h-3.5 accent-[#C0C0C0] cursor-pointer shrink-0"
                    />
                    <span className="text-[10px] leading-relaxed text-[#CBD5E1]">
                      <span className="text-red-300 mr-0.5">*</span>
                      Accetto la{" "}
                      <Link to="/privacy" target="_blank" className="text-[#C0C0C0] underline underline-offset-2 hover:text-white">
                        Privacy Policy
                      </Link>{" "}
                      e i{" "}
                      <Link to="/termini" target="_blank" className="text-[#C0C0C0] underline underline-offset-2 hover:text-white">
                        Termini
                      </Link>{" "}
                      per il trattamento dei dati d'ordine.
                    </span>
                  </label>
                  <label className="flex items-start gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={marketingOk}
                      onChange={(e) => setMarketingOk(e.target.checked)}
                      data-testid="cart-marketing-checkbox"
                      className="mt-0.5 w-3.5 h-3.5 accent-[#C0C0C0] cursor-pointer shrink-0"
                    />
                    <span className="text-[10px] leading-relaxed text-[#94A3B8]">
                      <span className="text-[9px] tracking-[0.24em] uppercase text-emerald-300/70 mr-1">Opzionale</span>
                      Autorizzo comunicazioni marketing, offerte e codici sconto.
                    </span>
                  </label>
                  {consentErr && (
                    <p data-testid="cart-consent-error" className="text-[10px] text-red-300 px-2 py-1.5 rounded-sm bg-red-500/10 border border-red-500/20">
                      {consentErr}
                    </p>
                  )}
                </div>
                <button onClick={goToCheckout} data-testid="cart-checkout-cta"
                        className="inline-flex items-center justify-center gap-2 bg-[#C0C0C0] text-[#060A14] px-5 py-3.5 rounded-sm text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-white">
                  <Lock className="w-4 h-4" />
                  Vai alla cassa
                </button>
                <p className="text-center text-[10px] text-[#94A3B8] tracking-[0.16em] mt-1 flex items-center justify-center gap-1.5">
                  <ShieldCheck className="w-3 h-3" />
                  Pagamento sicuro con Stripe · carta e altri metodi.
                </p>
              </div>
            </footer>
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
};

export default CartDrawer;
